import React from 'react'
import PropTypes from 'prop-types'
import SmartLink from '../SmartLink'
import styles from './styles.module.css'

// `productCard` rich-text block: an in-article "buy this product" card used by
// guide/blog posts (first use: /aluminum-sim-racing-cockpit-guide -> GT-2).
//
// PURE and RSC-safe by construction: no hooks, no context, no price tokens. The
// ONLY client/server difference in a rich-text block is the image leaf, so the
// caller injects it (`ImgComponent` = client <Img> from SimpleBlockContent, or
// <ImgServer> from SimpleBlockContentServer). Both leaves share ONE resolver
// (components/Img/resolve.js) so the DOM is byte-identical on either path, which
// keeps the block OUT of RenderSectionsServer's SBC_UNSUPPORTED_BLOCK_TYPES set
// (a page carrying it stays on the 0-JS server path).
//
// Deliberately price-free: the guide lives at a bare (region-less) URL, so no
// region price bundle is fetched for it. The CTA hands off to the product page,
// which owns price, colours and stock.
//
// Dataset shape (content/dataset.ndjson, inside a portable-text array):
//   { _type:'productCard', _key, image:{_type:'image', asset:{_ref}}, eyebrow?,
//     title, bullets:[string], ctaTitle, ctaHref, secondaryTitle?, secondaryHref? }
// `image` is pre-resolved to a ResolvedImage descriptor by the #67 walker.
function ProductCard({ value, ImgComponent }) {
  const {
    image,
    imageAlt,
    eyebrow,
    title,
    bullets,
    ctaTitle,
    ctaHref,
    secondaryTitle,
    secondaryHref,
  } = value || {}

  if (!title || !ctaHref) return null

  const hasImage = Boolean(image && image.src && ImgComponent)
  const list = Array.isArray(bullets) ? bullets.filter(Boolean) : []

  return (
    <aside className={styles.card} aria-label={title}>
      {hasImage && (
        <div className={styles.media}>
          <ImgComponent
            image={image}
            alt={imageAlt || title}
            layout="responsive"
            sizes="(max-width: 767px) 100vw, 640px"
            objectFit="cover"
          />
        </div>
      )}
      <div className={styles.body}>
        {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
        <p className={styles.title}>{title}</p>
        {list.length > 0 && (
          <ul className={styles.specs}>
            {list.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        <div className={styles.actions}>
          <SmartLink href={ctaHref} className={styles.cta}>{ctaTitle || 'Buy now'}</SmartLink>
          {secondaryTitle && secondaryHref && (
            <SmartLink href={secondaryHref} className={styles.secondary}>{secondaryTitle}</SmartLink>
          )}
        </div>
      </div>
    </aside>
  )
}

ProductCard.propTypes = {
  value: PropTypes.object,
  ImgComponent: PropTypes.elementType,
}

export default ProductCard
